import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Switch, message, Tag, Dropdown,
} from "antd";
import {
  PlusOutlined, ReloadOutlined, MoreOutlined, UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import BulkUploadModal from "../../components/common/BulkUploadModal";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";
import { EMI_STATUS_OPTIONS, formatOmr } from "./constants";
import {
  listEmiAgreements, getEmiKpis, createEmiAgreement, updateEmiAgreement,
  pauseEmiAgreement, resumeEmiAgreement, completeEmiAgreement,
  forecloseEmiAgreement, deleteEmiAgreement, exportEmiAgreements,
  downloadEmiTemplate, uploadEmiAgreements,
} from "../../services/payroll.service";

const { TextArea } = Input;

const KPI_CARDS = [
  { key: "all", label: "All" },
  { key: "upcoming", label: "Upcoming" },
  { key: "current", label: "Current" },
  { key: "paused", label: "Paused" },
  { key: "completed", label: "Completed" },
  { key: "foreclosed", label: "Foreclosed" },
  { key: "activePartners", label: "Active Partners" },
  { key: "inactivePartners", label: "In-active Partners" },
];

const STATUS_COLORS = {
  upcoming: "blue",
  current: "green",
  paused: "orange",
  completed: "default",
  foreclosed: "red",
};

export default function EMIManagementTab() {
  const { canEdit, canDelete } = usePermission("payrollExpenses");
  // Only types flagged emiEligible in Settings can back an instalment plan —
  // the backend rejects the rest, so the dropdown must not offer them.
  const { options: emiTypeOptions } = useSettingsDropdown("expense-emi-types", {
    filter: (item) => item.emiEligible,
  });
  const { options: workTypeOptions } = useSettingsDropdown("work-types");
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState();

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [allPartners, setAllPartners] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState();
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      status: status || undefined,
      ...extra,
    }),
    [search, status]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listEmiAgreements(params({ page, per_page: pageSize })),
        getEmiKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load EMI agreements");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      setAllPartners(Boolean(record.allPartners));
      form.setFieldsValue({
        expenseType: record.expenseType?._id,
        workType: record.workType?._id,
        project: record.project?._id,
        totalAmount: record.totalAmount,
        noOfInstallments: record.noOfInstallments,
        startDate: record.startDate ? dayjs(record.startDate) : null,
        allPartners: record.allPartners,
        riders: (record.riders || []).map((r) => r._id),
        description: record.description,
      });
    } else {
      setAllPartners(false);
      form.resetFields();
      form.setFieldsValue({ startDate: dayjs(), allPartners: false });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        startDate: values.startDate.format("YYYY-MM-DD"),
        allPartners: Boolean(values.allPartners),
        riders: values.allPartners ? [] : values.riders || [],
      };
      if (modal.record) {
        await updateEmiAgreement(modal.record._id, payload);
        message.success("EMI agreement updated");
      } else {
        await createEmiAgreement(payload);
        message.success("EMI agreement created");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save EMI agreement");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (fn, record, successMessage) => {
    try {
      await fn(record._id);
      message.success(successMessage);
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Action failed");
    }
  };

  const handleForeclose = (record) => {
    let reason = "";
    Modal.confirm({
      title: "Foreclose EMI Agreement",
      content: (
        <div>
          <p>Remaining instalments will be written off. This cannot be undone.</p>
          <Input.TextArea
            rows={3}
            placeholder="Reason for foreclosure"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: "Foreclose",
      okType: "danger",
      onOk: async () => {
        if (!reason.trim()) {
          message.error("Foreclosure reason is required");
          return Promise.reject(new Error("reason required"));
        }
        try {
          await forecloseEmiAgreement(record._id, reason.trim());
          message.success("EMI agreement foreclosed");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not foreclose agreement");
        }
      },
    });
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete EMI Agreement",
      content: `Delete the ${record.expenseTypeName} agreement?`,
      onConfirm: () => runAction(deleteEmiAgreement, record, "EMI agreement deleted"),
    });
  };

  const rowMenu = (record) => {
    const terminal = ["completed", "foreclosed"].includes(record.status);
    return {
      items: [
        record.status === "paused"
          ? { key: "resume", label: "Resume" }
          : { key: "pause", label: "Pause", disabled: terminal },
        { key: "complete", label: "Mark Completed", disabled: terminal },
        { key: "foreclose", label: "Foreclose", danger: true, disabled: terminal },
      ],
      onClick: ({ key }) => {
        if (key === "pause") runAction(pauseEmiAgreement, record, "Agreement paused");
        if (key === "resume") runAction(resumeEmiAgreement, record, "Agreement resumed");
        if (key === "complete") runAction(completeEmiAgreement, record, "Agreement completed");
        if (key === "foreclose") handleForeclose(record);
      },
    };
  };

  const columns = [
    { title: "Expense Type", dataIndex: "expenseTypeName" },
    { title: "Work Type", dataIndex: "workTypeName" },
    { title: "Project", dataIndex: "projectName" },
    {
      title: "Partners",
      dataIndex: "partnerNames",
      ellipsis: true,
      render: (value, r) =>
        r.allPartners ? <Tag color="blue">All Partners</Tag> : value || "—",
    },
    { title: "Total (OMR)", dataIndex: "totalAmount", align: "right", render: formatOmr },
    { title: "Instalments", dataIndex: "noOfInstallments", align: "right" },
    {
      title: "Monthly (OMR)",
      dataIndex: "monthlyInstallment",
      align: "right",
      render: formatOmr,
    },
    { title: "Instalments Paid", dataIndex: "installmentsPaid", align: "right" },
    {
      title: "Remaining Balance",
      dataIndex: "remainingBalance",
      align: "right",
      render: formatOmr,
    },
    { title: "Next Deduction", dataIndex: "nextDeductionDateLabel", render: (v) => v || "—" },
    { title: "Start", dataIndex: "startDateLabel" },
    { title: "End", dataIndex: "endDateLabel" },
    {
      title: "Status",
      dataIndex: "status",
      render: (value, r) => (
        <Tag color={STATUS_COLORS[value] || "default"}>{r.statusLabel}</Tag>
      ),
    },
    ...(canEdit
      ? [
          {
            title: "",
            key: "more",
            width: 48,
            render: (_, record) => (
              <Dropdown menu={rowMenu(record)} trigger={["click"]}>
                <Button type="text" size="small" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <KPIGrid minWidth={140}>
        {KPI_CARDS.map((card) => (
          <KPICard key={card.key} label={card.label} value={kpis[card.key]} />
        ))}
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search description"
          allowClear
          style={{ width: 240 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 170 }}
          options={EMI_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="EMI_Management"
          onExport={() => exportEmiAgreements(params())}
        />
        {canEdit && (
          <>
            <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
              Create EMI
            </Button>
          </>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit EMI Agreement" : "Create EMI Agreement"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={620}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="expenseType"
            label="Expense Type"
            rules={[{ required: true }]}
            extra="Only types marked EMI Eligible in Settings appear here."
          >
            <Select options={emiTypeOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="workType" label="Work Type">
            <Select options={workTypeOptions} allowClear />
          </Form.Item>
          <Form.Item name="project" label="Project">
            <Select options={projectOptions} allowClear />
          </Form.Item>
          <Form.Item name="totalAmount" label="Total Amount (OMR)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item
            name="noOfInstallments"
            label="No. of Instalments"
            rules={[{ required: true }]}
            extra="Monthly instalment and end date are calculated from these values."
          >
            <InputNumber style={{ width: "100%" }} min={1} precision={0} />
          </Form.Item>
          <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="allPartners" label="Apply to all partners" valuePropName="checked">
            <Switch onChange={setAllPartners} />
          </Form.Item>
          {!allPartners && (
            <Form.Item
              name="riders"
              label="Partners"
              rules={[{ required: true, message: "Select at least one partner" }]}
            >
              <Select
                mode="multiple"
                showSearch
                filterOption={false}
                onSearch={onRiderSearch}
                loading={ridersLoading}
                options={riderOptions}
                placeholder="Search by partner ID or name"
              />
            </Form.Item>
          )}
          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <BulkUploadModal
        open={uploadOpen}
        title="Upload EMI Agreements"
        templateName="EZYGO_EMI_Upload_Template"
        hint="CSV with Partner Id, Total Amount, No of Instalments and Start Date. One agreement per row."
        extra={
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#6b7280" }}>
              Expense type for every agreement in this file
            </div>
            <Select
              placeholder="Select an EMI-eligible expense type"
              options={emiTypeOptions}
              value={uploadType}
              onChange={setUploadType}
              style={{ width: "100%" }}
            />
          </div>
        }
        onDownloadTemplate={downloadEmiTemplate}
        onUpload={(file) => {
          if (!uploadType) {
            return Promise.reject(new Error("Select an expense type for this upload"));
          }
          return uploadEmiAgreements(file, { expenseType: uploadType });
        }}
        onClose={() => setUploadOpen(false)}
        onSuccess={fetchAll}
      />
    </div>
  );
}
